/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import { FormProvider, useFormState } from 'react-hook-form';
import { Text, TextVariant } from '@naf/text';
import { UserInteraction, UserInteractionVariant } from '@naf/user-interaction';
import VehicleIcon from '@components/icons/VehicleIcon';
import useSelector from '@redux/typedHooks';
import BreadCrumb from '@components/breadcrumb/BreadCrumb';
import { funnelPayload } from '@appTypes/gtmFunnel';
import { useSendGTMEventOnce } from '@hooks/GTM/useSendGTMEventOnce';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { mapSimbaVariant } from '@utils/simba';
import { Comments } from './Comments';
import { Confirmation } from './Confirmation';
import { useServiceCalculator } from '../context/ServiceCalculatorContext';
import * as S from '../Styles';

interface ManualRequestProps {
  documentId: string;
}

export const ManualRequest = ({ documentId }: ManualRequestProps) => {
  const trackCTA = useTrackCTA();
  const submitButtonVariant = mapSimbaVariant('signature');
  const resetButtonVariant = mapSimbaVariant('outline');
  const { customer } = useSelector((state) => state.myMembership.customerInformation.data);
  const { vehicleForm, feedbackForm, sendFeedbackForm, resetState } = useServiceCalculator();
  const { isSubmitting, isSubmitted, isSubmitSuccessful } = useFormState({
    control: feedbackForm.control,
  });
  const { licensePlateNumber, mileage, vehicleName } = vehicleForm.getValues();
  const vehicleText = `${licensePlateNumber} ${vehicleName}, ${mileage} km`;

  const [isFeedbackSent, setIsFeedbackSent] = useState(false);

  const onSubmit = async () => {
    const { comments } = feedbackForm.getValues();
    if (customer?.email) {
      const isSent = await sendFeedbackForm(customer.email, comments, vehicleText, documentId);
      setIsFeedbackSent(isSent);
    }
  };

  useSendGTMEventOnce(funnelPayload('Servicekalkulator', 4), []);

  return isSubmitted && isSubmitSuccessful && isFeedbackSent ? (
    <Confirmation />
  ) : (
    <S.StyledWrapper>
      <S.StyledInnerContent>
        <BreadCrumb paddingTop={false} />
        <Text variant={TextVariant.Header1}>Servicekalkulator - Elbil</Text>
        <UserInteraction className="full-width" title="Kan ikke kalkulere prisen" variant={UserInteractionVariant.Info}>
          Det kan være flere årsaker til at vi ikke kan gi deg en pris nå. Send oss en forespørsel med beskrivelse av
          hva du ønsker, så kommer vi tilbake til deg med et tilbud per e-post.
        </UserInteraction>

        <Text variant={TextVariant.Header2} style={{ marginTop: 40 }}>
          Få serviceforslag og prisestimat på e-post
        </Text>
        <Text variant={TextVariant.BodyText} style={{ marginBottom: 32 }}>
          Normal behandlingstid er 1-2 virkedager. I perioder med stor pågang kan det ta lenger tid.
        </Text>
        <S.StyledVehicleText>
          <VehicleIcon />
          <Text variant={TextVariant.BodyTextHeader}>{vehicleText}</Text>
        </S.StyledVehicleText>
        <S.UserInfoContainer>
          <Text variant={TextVariant.BodyText} style={{ marginBottom: 32, marginTop: 8 }}>
            Din e-post:
          </Text>
          <Text variant={TextVariant.BodyText} style={{ fontWeight: 700, marginTop: 8 }}>
            {customer?.email}
          </Text>
        </S.UserInfoContainer>
        <FormProvider {...feedbackForm}>
          <form onSubmit={feedbackForm.handleSubmit(onSubmit)}>
            <Comments name="comments" control={feedbackForm.control} />
            <S.StyledBottomButton
              type="submit"
              variant={submitButtonVariant.variant}
              color={submitButtonVariant.color}
              shade={submitButtonVariant.shade}
              isLoading={isSubmitting}
              onClick={() => trackCTA({ cta_position: 'content', text: 'Send forespørsel' })}
            >
              Send forespørsel
            </S.StyledBottomButton>
            {isSubmitted && !isFeedbackSent && (
              <S.StyledUserInteractionError
                title="Oops, nå kjørte vi oss fast"
                button={{
                  type: 'submit',
                  onClick: () => {
                    trackCTA({ cta_position: 'content', text: 'Prøv igjen' });
                    feedbackForm.handleSubmit(onSubmit);
                  },
                  children: 'Prøv igjen',
                }}
                variant="error"
              />
            )}
          </form>
        </FormProvider>
      </S.StyledInnerContent>
      <S.StyledBottomButton
        type="button"
        variant={resetButtonVariant.variant}
        color={resetButtonVariant.color}
        shade={resetButtonVariant.shade}
        onClick={() => {
          trackCTA({ cta_position: 'content', text: 'Kalkulér på nytt' });
          resetState();
        }}
      >
        Kalkulér på nytt
      </S.StyledBottomButton>
    </S.StyledWrapper>
  );
};
